// src/components/Modals.jsx
// Sprint 4c:
//   - NewMatterModal: cost checkbox removed (cost disclosed at run time, not matter creation)
//   - RunConfirmModal: new — confirms $50 charge immediately before Stripe redirect
//   - FeedbackModal: unchanged

import { useState } from 'react';

const GOLD = '#C8A84B';
const DARK = '#0D1B2A';

// ── New Matter Modal ──────────────────────────────────────────────────────────
// Shown when user clicks "+ New Matter".
// First-matter users are routed through Stripe card setup AFTER this modal.
// Subsequent users have their matter created directly.
// Cost is NOT charged here — it is charged when the user runs LitFit™.

export function NewMatterModal({ onConfirm, onClose, loading }) {
  const [name,          setName]          = useState('');
  const [retentionDays, setRetentionDays] = useState(30);

  const canSubmit = name.trim() && !loading;

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 22, fontWeight: 500, color: DARK, marginBottom: 8 }}>
        New matter
      </div>
      <div style={{ fontSize: 13, color: '#AAA', fontWeight: 300, marginBottom: 24 }}>
        Enter the matter name. You'll upload documents and run LitFit™ from the matter page.
      </div>

      <input
        autoFocus
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && canSubmit && onConfirm(name.trim(), retentionDays)}
        placeholder="e.g. Smith v KPMG"
        style={{
          width: '100%', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
          border: '1px solid #E0DDD6', borderRadius: 8, padding: '10px 14px',
          outline: 'none', color: '#222', marginBottom: 16,
          boxSizing: 'border-box',
        }}
      />

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', fontSize: 12.5, color: '#555', fontWeight: 400, marginBottom: 6 }}>
          Auto-delete this matter's files after a period of inactivity
        </label>
        <select
          value={retentionDays}
          onChange={e => setRetentionDays(Number(e.target.value))}
          style={{
            width: '100%', fontSize: 14, fontFamily: "'DM Sans', sans-serif",
            border: '1px solid #E0DDD6', borderRadius: 8, padding: '10px 14px',
            outline: 'none', color: '#222', boxSizing: 'border-box',
            background: '#FFF', cursor: 'pointer',
          }}
        >
          <option value={7}>7 days after last activity</option>
          <option value={14}>14 days after last activity</option>
          <option value={30}>30 days after last activity</option>
          <option value={90}>90 days after last activity</option>
          <option value={365}>365 days after last activity</option>
        </select>
        <div style={{ fontSize: 11, color: '#999', fontWeight: 300, lineHeight: 1.5, marginTop: 6 }}>
          Files are deleted this many days after your last run, upload, or change. Any activity resets the clock, and you can extend at any time.
        </div>
      </div>

      {/* ATTEST-1 — CDCA helper line (text-only, no tick) */}
      <div style={{ fontSize: 11.5, color: '#999', fontWeight: 300, lineHeight: 1.55 }}>
        If your client engagement terms need to be amended to authorise AI-assisted
        analysis, a pro-forma amendment is available{' '}
        <a href="/cdca-amendment.docx" style={{ color: GOLD, textDecoration: 'underline' }}>here</a>.
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
        <button onClick={onClose} style={{ ...ghostBtn }}>Cancel</button>
        <button
          disabled={!canSubmit}
          onClick={() => canSubmit && onConfirm(name.trim(), retentionDays)}
          style={{ ...primaryBtn, opacity: !canSubmit ? 0.6 : 1 }}
        >
          {loading ? 'Creating…' : 'Create matter →'}
        </button>
      </div>
    </Overlay>
  );
}

// ── Run Confirm Modal ─────────────────────────────────────────────────────────
// Shown immediately before Stripe redirect when user clicks "Run LitFit™".
// This is where the $50 cost disclosure lives — at the moment of charge.

export function RunConfirmModal({ matter, perspective, onConfirm, onClose }) {
  // ATTEST-1 (12 Jun 2026): single run attestation. Wording LOCKED — do not
  // edit without updating ATTEST_TEXT_VERSION and the backlog record.
  const [attest, setAttest] = useState(false);
  const ATTEST_TEXT_VERSION = 'ATTEST-1_v1_20260612';

  const handleConfirm = () => {
    if (!attest) return;
    onConfirm({
      text_version: ATTEST_TEXT_VERSION,
      attested:     true,
      attested_at:  new Date().toISOString(),
    });
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 500, color: DARK, marginBottom: 10 }}>
        Run LitFit™ analysis
      </div>

      <div style={{
        background: '#F7F5F0', borderRadius: 8, padding: '14px 16px',
        marginBottom: 20, borderLeft: `3px solid ${GOLD}`,
      }}>
        <div style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}>
          <strong style={{ color: DARK }}>{matter.matter_name}</strong>
          <br />
          Perspective: <strong>{perspective}</strong>
        </div>
      </div>

      <div style={{ fontSize: 13, color: '#555', lineHeight: 1.7, marginBottom: 18 }}>
        This will generate a Case Readiness Report. The cost is{' '}
        <strong style={{ color: DARK }}>$50 AUD</strong>, charged to your saved
        payment method. As a disbursement, this fee may be recoverable from your client.
      </div>

      {/* ATTEST-1 — attestation tick (gates Confirm) */}
      <label style={{ ...checkboxRow(attest) }}>
        <input
          type="checkbox"
          checked={attest}
          onChange={e => setAttest(e.target.checked)}
          style={{ marginTop: 3, accentColor: GOLD, flexShrink: 0 }}
        />
        <span style={{ fontSize: 12.5, color: '#333' }}>
          I am authorised to conduct this matter. Where any uploaded document is
          subject to a suppression or non-publication order, or to the implied
          (Harman) undertaking, I am satisfied that its use for this analysis is
          permitted.
        </span>
      </label>

      {/* ATTEST-1 — platform-voice helper (no tick) */}
      <div style={{ fontSize: 11, color: '#999', fontWeight: 300, lineHeight: 1.55, margin: '0 4px 22px' }}>
        Calderbank.ai operates as a closed, Australian-hosted environment in which
        documents are used solely to generate your report. The conditions are
        recorded in each run's Compliance Certificate.
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...ghostBtn }}>Cancel</button>
        <button
          onClick={handleConfirm}
          disabled={!attest}
          title={!attest ? 'Tick the attestation to continue' : ''}
          style={{ ...primaryBtn, opacity: !attest ? 0.55 : 1, cursor: !attest ? 'not-allowed' : 'pointer' }}
        >
          Confirm — Run LitFit™ →
        </button>
      </div>
    </Overlay>
  );
}

// ── Feedback Modal ────────────────────────────────────────────────────────────

export function FeedbackModal({ runId, score, onSubmit, onClose }) {
  const [rating,     setRating]    = useState(0);
  const [comment,    setComment]   = useState('');
  const [submitted,  setSubmitted] = useState(false);

  const handleSubmit = async () => {
    await onSubmit(runId, rating, comment);
    setSubmitted(true);
    setTimeout(onClose, 1500);
  };

  if (submitted) {
    return (
      <Overlay onClose={onClose}>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>✓</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 18, color: DARK }}>
            Thank you
          </div>
          <div style={{ fontSize: 13, color: '#AAA', marginTop: 8 }}>Your feedback helps improve LitFit™</div>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 500, color: DARK, marginBottom: 6 }}>
        How useful was this report?
      </div>
      {score != null && (
        <div style={{ fontSize: 13, color: '#C8A84B', marginBottom: 16 }}>
          LitFit™ {score.toFixed(1)} / 10
        </div>
      )}
      <div style={{ fontSize: 12, color: '#999', fontWeight: 300, marginBottom: 20, lineHeight: 1.5 }}>
        Your feedback helps improve LitFit™ accuracy. Please do not include client-specific information.
      </div>

      {/* Star rating */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n} onClick={() => setRating(n)}
            style={{
              width: 40, height: 40, borderRadius: 8, fontSize: 20, border: 'none',
              background: rating >= n ? 'rgba(200,168,75,0.15)' : '#F5F2EE',
              cursor: 'pointer', transition: 'all 0.15s',
            }}
          >★</button>
        ))}
      </div>

      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Optional comment (no client-specific details please)…"
        maxLength={200}
        style={{
          width: '100%', height: 80, fontSize: 13, fontFamily: "'DM Sans', sans-serif",
          border: '1px solid #E0DDD6', borderRadius: 8, padding: '10px 14px',
          outline: 'none', resize: 'none', color: '#333', marginBottom: 6,
          boxSizing: 'border-box',
        }}
      />
      <div style={{ fontSize: 11, color: '#CCC', textAlign: 'right', marginBottom: 20 }}>{comment.length}/200</div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ ...ghostBtn }}>Skip</button>
        <button
          disabled={rating === 0}
          onClick={handleSubmit}
          style={{ ...primaryBtn, opacity: rating === 0 ? 0.6 : 1 }}
        >Submit feedback</button>
      </div>
    </Overlay>
  );
}

// ── Shared ────────────────────────────────────────────────────────────────────

function Overlay({ children, onClose }) {
  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(13,27,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
    >
      <div style={{
        background: '#FFF', borderRadius: 14, padding: '32px 36px',
        width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        position: 'relative', boxSizing: 'border-box',
      }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', fontSize: 18, color: '#CCC', cursor: 'pointer', lineHeight: 1 }}
        >×</button>
        {children}
      </div>
    </div>
  );
}

const checkboxRow = (checked) => ({
  display: 'flex', alignItems: 'flex-start', gap: 12,
  padding: '12px 16px', marginBottom: 10, borderRadius: 8,
  border: `1px solid ${checked ? GOLD : '#E8E4DC'}`,
  background: checked ? 'rgba(200,168,75,0.05)' : '#FAFAF8',
  cursor: 'pointer', lineHeight: 1.55,
  transition: 'all 0.15s',
});

const primaryBtn = {
  background: '#0D1B2A', color: '#FFF', border: 'none',
  padding: '10px 20px', borderRadius: 8, fontSize: 13,
  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
};

const ghostBtn = {
  background: 'transparent', color: '#888',
  border: '1px solid #E0DDD6',
  padding: '10px 20px', borderRadius: 8, fontSize: 13,
  fontFamily: "'DM Sans', sans-serif", cursor: 'pointer',
};
